import React from "react";
import { styled } from "@mui/material/styles";
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Typography,
  Grid,
  Box,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { QuestionCollapsableElementProps } from "../../Utils/Types";
import ExamAnswers from "../Questionnaire/ExamAnswears";
import ExamExplanation from "../Questionnaire/ExamExplanation";
import { isMobile } from "../../Utils/Utils";
import ExamImage from "../Questionnaire/ExamImage";

/**
 * Styles for the Accordion component.
 */
const Accordion = styled(MuiAccordion)(() => ({
  background: 'rgba(255, 255, 255, 0.9)',
  borderRadius: '16px !important',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: 'none',
  marginBottom: '16px',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  backdropFilter: 'blur(10px)',
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
  "&.Mui-expanded": {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
  "&:hover": {
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.1)',
  },
}));

/**
 * Styles for the AccordionSummary component.
 */
const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
  color: 'white',
  minHeight: '72px',
  padding: theme.spacing(0, 3),
  borderRadius: '16px 16px 0 0',
  transition: 'all 0.3s ease',
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    display: "flex",
    margin: theme.spacing(2, 0),
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: 'white',
    transform: 'rotate(0deg)',
    transition: 'transform 0.3s ease',
    '&.Mui-expanded': {
      transform: 'rotate(180deg)',
    },
  },
  "&:hover": {
    background: 'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)',
  },
}));

/**
 * Styles for the AccordionDetails component.
 */
const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'rgba(255, 255, 255, 0.95)',
  borderTop: 'none',
}));

/**
 * A QuestionAccordionItem is a component that displays a single question and its explanation.
 * It uses the MUI Accordion component to expand and collapse the content.
 *
 * @param {QuestionCollapsableElementProps} props - The props for the component:
 *   question - The question object to display.
 *   panelId - The id to use for the accordion.
 *   expanded - Whether the accordion is expanded or not.
 *   onChange - The callback to call when the accordion is expanded or collapsed.
 * @returns {JSX.Element} The component.
 */
export default function QuestionAccordionItem({
  question,
  panelId,
  expanded,
  onChange,
}: QuestionCollapsableElementProps) {
  return (
    <Accordion expanded={expanded} onChange={onChange}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${panelId}-content`}
        id={`${panelId}-header`}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <ErrorOutlineIcon sx={{ fontSize: '1.5rem', opacity: 0.9 }} />
          <Typography 
            component="span" 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              lineHeight: 1.3,
              flex: 1
            }}
          >
            {question.questionText}
          </Typography>
          <Chip 
            label="Greșeală comună" 
            size="small"
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: 500,
              fontSize: '0.75rem'
            }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ p: 1 }}>
          <Grid container spacing={3}>
            {isMobile() && question?.imageUrl && (
              <Grid item xs={12}>
                <ExamImage imageUrl={question?.imageUrl} maxWidth="100%" />
              </Grid>
            )}
            <Grid
              item
              xs={12}
              md={question?.imageUrl ? 6 : 12}
              sx={{ display: 'flex', alignItems: 'flex-start' }}
            >
              <ExamAnswers
                question={question}
                selected={[]}
                disabled={true}
                showResult={true}
              />
            </Grid>
            {!isMobile() && question?.imageUrl && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <ExamImage imageUrl={question?.imageUrl} maxHeight="400px" />
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <ExamExplanation isCorrect={false} question={question} />
            </Grid>
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
